import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { colourForToken, enterAt, WIDGET_LOCALE } from '../shared';

/**
 * `plate` → `Plate`, `due_date` → `Due date`.
 *
 * A card's `fields` is an open map — whatever the author bound — so the
 * contract carries no label for it. The key is the only name there is, and a
 * bare `FTR-219` under a bare `Sofía León` is two strings nobody can tell
 * apart. Humanising the key is a guess, but it is a guess the reader can
 * check; printing the value alone is not.
 */
const labelForKey = (key) => {
  const words = String(key).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Records grouped by lifecycle state.
 *
 * ## Reading the contract (CT-5641)
 *
 * `BOARD` publishes `lanes[]`, and a lane carries **both** a `count` and the
 * `cards[]` behind it. That is the part this used to drop: the previous shape
 * was `columns[{label, count}]`, so a board could only ever say *how many*
 * repairs are open, never *which*.
 *
 * ## The lane header is deliberately quiet
 *
 * It was the other way round at first — the count set in large type, centred,
 * with the lane name as a badge under it. That reads as a stat row that
 * happens to have cards attached, and it spends the lane's vertical budget on
 * the one number that needs the least room.
 *
 * So: one line, the lane's name on the left, its count on the right, and all
 * the remaining height to the cards. The count still lines up across lanes —
 * it is in a fixed column — which is the only reason it wanted prominence.
 *
 * A lane with no cards says so. `count: 0, cards: []` is a real answer the
 * board is giving, and an empty box is indistinguishable from one that failed
 * to load.
 *
 * Each lane scrolls on its own. A board where one busy lane sets the height of
 * all the others is a board with four lanes' worth of whitespace.
 *
 * @param {object} props
 * @param {{ lanes: object[] }} props.data `BOARD`'s `lanes[]`, or the pre-v2 `columns[]`.
 * @param {(lane: object) => void} [props.onColumnClick]
 * @param {(card: object, lane: object) => void} [props.onCardClick]
 * @param {string} [props.className]
 */
function BoardWidget({ data, onColumnClick, onCardClick, className = '' }) {
  const lanes = useMemo(() => (data?.lanes ?? data?.columns ?? []).map((lane, index) => ({
    key: lane.key ?? lane.label ?? String(index),
    label: lane.label ?? lane.key ?? '',
    count: lane.count ?? 0,
    /*
      `display.lanes[].color_token` is where the contract puts a lane's
      colour, and this widget is handed `data`, not `display`. Read it off the
      lane when a caller does pass it through; neutral otherwise, which is
      honest — an invented hue per lane would be read as meaning something.
    */
    colour: colourForToken(lane.color_token),
    cards: (lane.cards ?? []).map((card, cardIndex) => ({
      key: card.row_key ?? String(cardIndex),
      title: card.title,
      subtitle: card.subtitle ?? '',
      fields: Object.entries(card.fields ?? {})
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({ key, label: labelForKey(key), value: String(value) })),
      raw: card,
    })),
    raw: lane,
  })), [data]);

  if (!lanes.length) return null;

  // One lane with cards puts every lane in the stacked arrangement: lanes that
  // line up their headers and then disagree about their height read as a
  // broken grid.
  const hasCards = lanes.some((lane) => lane.cards.length);

  return (
    <div className={`flex min-h-0 gap-2 ${className}`.trim()}>
      {lanes.map((lane, index) => {
        const enter = enterAt(index, 'scale');
        const header = (
          <>
            <span
              aria-hidden
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: lane.colour }}
            />
            <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-content">
              {lane.label}
            </span>
            <span className="shrink-0 text-[11px] font-semibold text-content-muted">
              {lane.count.toLocaleString(WIDGET_LOCALE)}
            </span>
          </>
        );

        const headerShell = 'flex w-full items-center gap-1.5 px-2 py-1.5';

        return (
          <div
            key={lane.key}
            className={`flex min-h-0 flex-1 flex-col rounded-lg border border-edge ${enter.className}`}
            style={enter.style}
          >
            {onColumnClick ? (
              <button
                type="button"
                onClick={() => onColumnClick(lane.raw)}
                className={`${headerShell} rounded-t-lg text-left hover:bg-surface-hover`}
              >
                {header}
              </button>
            ) : (
              <div className={headerShell}>{header}</div>
            )}

            {hasCards ? (
              <ul className="scrollbar-aimsos-auto m-0 flex min-h-0 flex-1 list-none flex-col gap-1.5 overflow-y-auto p-1.5 pt-0">
                {lane.cards.length ? lane.cards.map((card) => {
                  const body = (
                    <>
                      <span className="truncate text-[11px] font-semibold text-content">
                        {card.title}
                      </span>
                      {card.subtitle ? (
                        <span className="truncate text-[10px] text-content-secondary">
                          {card.subtitle}
                        </span>
                      ) : null}
                      {card.fields.map((field) => (
                        <span key={field.key} className="flex min-w-0 gap-1 text-[10px]">
                          <span className="shrink-0 text-content-faint">{field.label}</span>
                          <span className="truncate text-content-muted">{field.value}</span>
                        </span>
                      ))}
                    </>
                  );

                  const cardShell =
                    'flex w-full min-w-0 flex-col gap-px rounded-md border border-edge bg-surface px-2 py-1.5';

                  return (
                    <li key={card.key} className="min-w-0">
                      {onCardClick ? (
                        <button
                          type="button"
                          onClick={() => onCardClick(card.raw, lane.raw)}
                          className={`${cardShell} text-left hover:bg-surface-hover`}
                        >
                          {body}
                        </button>
                      ) : (
                        <div className={cardShell}>{body}</div>
                      )}
                    </li>
                  );
                }) : (
                  <li className="px-1 py-1 text-[10px] italic text-content-faint">no cards</li>
                )}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

BoardWidget.propTypes = {
  /** `BOARD`'s `lanes[{key, label, count, cards}]`. Pre-v2 `columns[]` still renders. */
  data: PropTypes.shape({
    lanes: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        /** Not in `data` — read when a caller threads `display.lanes[]` through. */
        color_token: PropTypes.string,
        cards: PropTypes.arrayOf(
          PropTypes.shape({
            row_key: PropTypes.string,
            title: PropTypes.string,
            subtitle: PropTypes.string,
            fields: PropTypes.object,
          }),
        ),
      }),
    ),
    /** The pre-v2 shape, still accepted. */
    columns: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onColumnClick: PropTypes.func,
  onCardClick: PropTypes.func,
  className: PropTypes.string,
};

export default memo(BoardWidget);
